import { useState, useEffect, useRef } from "react";
import HomeScreen from "./components/HomeScreen";
import BreachRegister from "./components/BreachRegister";
import storage, { COLLECTIONS } from "./storage";
import nis2Standard from "./standards/nis2";
import iso27001Standard from "./standards/iso27001";
import gdprStandard from "./standards/gdpr";
import nistCsfStandard from "./standards/nist-csf";
import soc2Standard from "./standards/soc2";
import ccmStandard from "./standards/ccm";

const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:wght@400;500&display=swap');
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
@media print { .no-print { display: none !important; } }
`;

// ─── THEME ────────────────────────────────────────────────────────────────────
const TH = {
  bg:           "#eeeae3",
  bgCard:       "#e5e1d9",
  surface:      "#faf9f6",
  surfaceWhite: "#ffffff",
  border:       "#d6d2ca",
  borderFocus:  "#1a5c3a",
  text:         "#0f2235",
  textSub:      "#374151",
  textMuted:    "#6b7280",
  textIcon:     "#9ca3af",
  accent:       "#1a5c3a",
  navy:         "#0f2235",
};

// ─── I18N ─────────────────────────────────────────────────────────────────────
const T = {
  es: {
    fieldCompany:   "Nombre de la empresa",
    placeholderCo:  "Ej. Acme Corp",
    statDomains:    "Dominios",
    statQuestions:  "Preguntas",
    statTime:       "Estimado",
    startBtn:       "Iniciar evaluación →",
    progress:       "PROGRESO",
    critical:       "CRÍTICO",
    prevBtn:        "← Anterior",
    nextBtn:        "Siguiente dominio →",
    generateBtn:    "Generar informe →",
    ansYes:         "Sí",
    ansPartial:     "Parcial",
    ansNo:          "No",
    ansNA:          "N/A",
    loadingMsg:     (n) => `Analizando ${n} gaps y generando recomendaciones específicas…`,
    editBtn:        "← Editar respuestas",
    newBtn:         "Nueva evaluación",
    detailBtn:      "Ver detalle completo →",
    detailTitle:    "Detalle de hallazgos",
    filterAll:      "Todos",
    filterGaps:     "Solo brechas",
    filterOk:       "Solo cumplen",
    filterCritical: "Solo críticas",
    statusOk:       "✓ Cumple",
    statusPartial:  "⚠ Parcial",
    statusNo:       "✗ No cumple",
    statusNA:       "— N/A",
    allTools:       "Todas las herramientas",
    scoreLabels: {
      null:    { label: "Sin datos",  desc: "" },
      advanced:{ label: "Avanzado",   desc: "Cumplimiento sustancial" },
      basic:   { label: "Básico",     desc: "Cumplimiento parcial con gaps relevantes" },
      initial: { label: "Inicial",    desc: "Madurez insuficiente, riesgo elevado" },
      critical:{ label: "Crítico",    desc: "Incumplimiento grave, exposición severa" },
    },
  },
  en: {
    fieldCompany:   "Company name",
    placeholderCo:  "e.g. Acme Corp",
    statDomains:    "Domains",
    statQuestions:  "Questions",
    statTime:       "Estimated",
    startBtn:       "Start assessment →",
    progress:       "PROGRESS",
    critical:       "CRITICAL",
    prevBtn:        "← Previous",
    nextBtn:        "Next domain →",
    generateBtn:    "Generate report →",
    ansYes:         "Yes",
    ansPartial:     "Partial",
    ansNo:          "No",
    ansNA:          "N/A",
    loadingMsg:     (n) => `Analysing ${n} gaps and generating specific recommendations…`,
    editBtn:        "← Edit answers",
    newBtn:         "New assessment",
    detailBtn:      "View full detail →",
    detailTitle:    "Findings detail",
    filterAll:      "All",
    filterGaps:     "Gaps only",
    filterOk:       "Compliant only",
    filterCritical: "Critical only",
    statusOk:       "✓ Compliant",
    statusPartial:  "⚠ Partial",
    statusNo:       "✗ Non-compliant",
    statusNA:       "— N/A",
    allTools:       "All tools",
    scoreLabels: {
      null:    { label: "No data",    desc: "" },
      advanced:{ label: "Advanced",   desc: "Substantial compliance" },
      basic:   { label: "Basic",      desc: "Partial compliance with relevant gaps" },
      initial: { label: "Initial",    desc: "Insufficient maturity, high risk" },
      critical:{ label: "Critical",   desc: "Serious non-compliance, severe exposure" },
    },
  },
};

const STANDARDS = [nis2Standard, iso27001Standard, gdprStandard, nistCsfStandard, soc2Standard, ccmStandard];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function scoreToLevel(score, lang) {
  const sl = T[lang || "es"].scoreLabels;
  if (score === null) return { ...sl.null,    color: "#6b7280" };
  if (score >= 80)   return { ...sl.advanced, color: "#1a5c3a" };
  if (score >= 60)   return { ...sl.basic,    color: "#b07a00" };
  if (score >= 30)   return { ...sl.initial,  color: "#c05a00" };
  return               { ...sl.critical,  color: "#b00020" };
}

// ─── DATE HELPER ─────────────────────────────────────────────────────────────
function formatDate(lang) {
  const d = new Date();
  const months = {
    es: ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"],
    en: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
  };
  return `${d.getDate()} ${months[lang || "es"][d.getMonth()]} ${d.getFullYear()}`;
}

// ─── NAVBAR ───────────────────────────────────────────────────────────────────
function Navbar({ lang, onLangToggle, onHome, showBreadcrumb }) {
  const L = lang || "es";
  const [narrow, setNarrow] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return (
    <div className="no-print" style={{
      height: 52, background: "#eeeae3", borderBottom: "1px solid #d6d2ca",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: narrow ? "0 0.75rem" : "0 1.5rem", position: "sticky", top: 0, zIndex: 50,
      boxSizing: "border-box",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: narrow ? "0.75rem" : "2rem" }}>
        <div
          style={{ display: "flex", alignItems: "baseline", gap: "0.35rem", cursor: onHome ? "pointer" : "default", userSelect: "none" }}
          onClick={onHome}
        >
          <span style={{ fontFamily: "'Syne',sans-serif", color: "#0f2235", fontWeight: 800, fontSize: "1rem" }}>AT.</span>
          <span style={{ fontFamily: "'Syne',sans-serif", color: "#1a5c3a", fontWeight: 400, fontSize: "0.9rem" }}>· tools</span>
        </div>
        {showBreadcrumb && onHome && (
          <button onClick={onHome} style={{
            background: "none", border: "none", cursor: "pointer",
            fontFamily: "'DM Mono',monospace", fontSize: "0.68rem",
            color: "#6b7280", letterSpacing: "0.04em", padding: 0,
          }}>
            {narrow ? "←" : `← ${L === "en" ? "All tools" : "Todas las herramientas"}`}
          </button>
        )}
      </div>
      <div style={{ display: "flex", border: "1px solid #d6d2ca", borderRadius: 6, overflow: "hidden" }}>
        {[
          { code: "es", label: "ESPAÑOL" },
          { code: "en", label: "ENGLISH" },
        ].map(({ code, label }) => {
          const active = (lang || "es") === code;
          return (
            <button key={code} onClick={() => onLangToggle(code)} style={{
              background: active ? "#0f2235" : "transparent",
              color: active ? "#ffffff" : "#6b7280",
              border: "none", borderRadius: 0,
              padding: "0.3rem 0.75rem", cursor: "pointer",
              fontFamily: "'DM Mono',monospace", fontSize: "0.62rem",
              letterSpacing: "0.06em", transition: "all 0.15s",
            }}>
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── EYEBROW ─────────────────────────────────────────────────────────────────
function Eyebrow({ text }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
      <div style={{ width: 28, height: 2, background: "#1a5c3a", flexShrink: 0 }} />
      <span style={{ fontFamily: "'DM Mono',monospace", fontSize: "0.62rem", color: "#1a5c3a", letterSpacing: "0.15em", textTransform: "uppercase" }}>{text}</span>
    </div>
  );
}

// ─── MARKDOWN RENDERER ────────────────────────────────────────────────────────
function renderInline(raw, lang, artUrlFn) {
  if (lang === "es") raw = raw.replace(/\bgaps?\b/gi, m => m.toLowerCase() === "gap" ? "brecha" : "brechas");
  const tokens = raw.split(/(\*\*[^*\n]+\*\*|\[Art\.[^\]\n]+\])/g);
  return tokens.map((tok, j) => {
    if (tok.startsWith("**") && tok.endsWith("**")) return <strong key={j}>{tok.slice(2, -2)}</strong>;
    if (tok.match(/^\[Art\.[^\]]+\]$/)) {
      const ref = tok.slice(1, -1);
      return <a key={j} href={artUrlFn(ref, lang)} target="_blank" rel="noopener noreferrer"
        title={lang === "es" ? `Ver ${ref} en EUR-Lex` : `View ${ref} on EUR-Lex`}
        style={{ color: "#1a5c3a", textDecoration: "none", borderBottom: "1px dotted #1a5c3a", fontFamily: "'DM Mono',monospace", fontSize: "0.85em", whiteSpace: "nowrap" }}>
        {ref} ↗
      </a>;
    }
    return tok.replace(/\*/g, "");
  });
}

function MD({ text, lang, artUrlFn }) {
  if (!text) return null;
  const L = lang || "es";
  const fn = artUrlFn || ((r) => r);
  return text.split("\n").map((line, i) => {
    const tr = line.trim();
    if (!tr || tr.startsWith("# ")) return <div key={i} style={{ height: "0.4rem" }} />;
    if (tr.startsWith("## "))
      return (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.75rem", margin: "1.6rem 0 0.6rem" }}>
          <div style={{ width: 28, height: 2, background: "#1a5c3a", flexShrink: 0 }} />
          <span style={{ fontFamily: "'Syne',sans-serif", fontSize: "0.82rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "#1a5c3a", fontWeight: 700 }}>{tr.slice(3)}</span>
        </div>
      );
    const numMatch = tr.match(/^\*?(\d+)\.\s+([\s\S]*)/);
    if (numMatch)
      return <li key={i} style={{ marginLeft: "1.4rem", marginBottom: "0.5rem", color: "#374151", fontSize: "0.93rem", lineHeight: 1.7, listStyleType: "decimal" }}>{renderInline(numMatch[2], L, fn)}</li>;
    if (tr.startsWith("- ") || tr.match(/^[•·]\s/))
      return <li key={i} style={{ marginLeft: "1.4rem", marginBottom: "0.5rem", color: "#374151", fontSize: "0.93rem", lineHeight: 1.7 }}>{renderInline(tr.replace(/^[-•·]\s/, ""), L, fn)}</li>;
    return <p key={i} style={{ color: "#374151", fontSize: "0.93rem", lineHeight: 1.75, margin: "0 0 0.3rem" }}>{renderInline(tr, L, fn)}</p>;
  });
}

// ─── MICRO COMPONENTS ─────────────────────────────────────────────────────────
function LabelEl({ children }) {
  return <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.62rem", color:"#555f6e", letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:"0.4rem" }}>{children}</div>;
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div>
      <LabelEl>{label}</LabelEl>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width:"100%", background:"#ffffff", border:"1px solid #d6d2ca", borderRadius:6, padding:"0.7rem 1rem", color:TH.text, fontFamily:"'Syne',sans-serif", fontSize:"0.88rem", outline:"none", boxSizing:"border-box", transition:"border-color 0.2s" }}
        onFocus={e => e.target.style.borderColor="#1a5c3a"}
        onBlur={e => e.target.style.borderColor="#d6d2ca"} />
    </div>
  );
}

// ─── DETAIL VIEW ──────────────────────────────────────────────────────────────
function DetailView({ standard, answers, entityType, lang, t, companyName, sector, country, globalScore, globalLevel, onBack, onHome, onLangToggle, assessmentMode }) {
  const noET = standard?.hasEntityType === false;
  const filterQ = (q, et) => {
    const roleOk = noET ? true : (standard.scopeFilter ? standard.scopeFilter(q.scope, et, standard.modeSelector ? assessmentMode : undefined) : (q.scope === "all" || q.scope === et.toLowerCase() || et === "Esencial" || et === "Essential"));
    const modeOk = !standard.modeSelector || assessmentMode === "full" || q.lite === true;
    return roleOk && modeOk;
  };
  const { DOMAINS, getQuestions, calcDomainScore, artUrl } = standard
    ? {
        DOMAINS: standard.domains,
        getQuestions: (d, et) => noET ? (standard.modeSelector && assessmentMode === "lite" ? d.questions.filter(q => q.lite) : d.questions) : d.questions.filter(q => filterQ(q, et)),
        calcDomainScore: (d, ans, et) => {
          const qs = noET ? (standard.modeSelector && assessmentMode === "lite" ? d.questions.filter(q => q.lite) : d.questions) : d.questions.filter(q => filterQ(q, et));
          let total = 0, earned = 0;
          for (const q of qs) {
            const a = ans[q.id]; if (!a || a === "na") continue;
            const w = q.critical ? 2 : 1;
            total += w; earned += (a === "yes" ? 1 : a === "partial" ? 0.5 : 0) * w;
          }
          return total === 0 ? null : Math.round((earned / total) * 100);
        },
        getGaps: (d, ans, et) => d.questions.filter(q => filterQ(q, et) && (ans[q.id] === "no" || ans[q.id] === "partial")),
        artUrl: standard.artUrl || ((r) => r),
      }
    : { DOMAINS: [], getQuestions: () => [], calcDomainScore: () => null, getGaps: () => [], artUrl: (r) => r };

  const [filter, setFilter] = useState("all");
  const [expandedDomains, setExpandedDomains] = useState({});
  function toggleDomain(id) { setExpandedDomains(prev => ({ ...prev, [id]: !prev[id] })); }

  const showRefPrefix = standard?.id === "iso27001";
  const showSoc2RefPrefix = standard?.id === "soc2" || standard?.id === "ccm";
  const allRows = [];
  for (const d of DOMAINS) {
    for (const q of getQuestions(d, entityType)) {
      const ans = answers[q.id];
      const refNum = q.ref.replace(/^ISO 27001 §?/, "");
      const questionText = showRefPrefix ? `${refNum}. ${q[lang]}` : showSoc2RefPrefix ? `${q.ref} · ${q[lang]}` : q[lang];
      allRows.push({
        domainId: d.id, domainLabel: d.label[lang], domainArticle: d.article, icon: d.icon,
        question: questionText, ref: q.ref, critical: q.critical,
        fix: q.fix ? q.fix[lang] : null,
        status: ans === "yes" ? "ok" : ans === "partial" ? "partial" : ans === "no" ? "no" : "na",
      });
    }
  }

  const filtered = allRows.filter(r => {
    if (filter === "gaps")     return r.status === "no" || r.status === "partial";
    if (filter === "ok")       return r.status === "ok";
    if (filter === "critical") return r.critical && (r.status === "no" || r.status === "partial");
    return true;
  });

  const grouped = DOMAINS.map(d => ({
    id: d.id, label: d.label[lang], article: typeof d.article === 'object' ? d.article[lang] : d.article, icon: d.icon,
    score: calcDomainScore(d, answers, entityType),
    rows: filtered.filter(r => r.domainId === d.id),
    allRows: allRows.filter(r => r.domainId === d.id),
  })).filter(g => g.rows.length > 0);

  const statusStyle = (status, critical) => {
    if (status === "ok")      return { color: "#1a5c3a", label: t.statusOk };
    if (status === "partial") return { color: "#b07a00", label: t.statusPartial };
    if (status === "no")      return { color: critical ? "#b00020" : "#c05a00", label: t.statusNo };
    return { color: TH.textMuted, label: t.statusNA };
  };

  const totalRows = allRows.length;
  const gapRows   = allRows.filter(r => r.status === "no" || r.status === "partial").length;
  const okRows    = allRows.filter(r => r.status === "ok").length;

  function exportExcel() {
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
    script.onload = () => {
      const XLSX = window.XLSX;
      const wb = XLSX.utils.book_new();
      const summaryHeader = lang === "es"
        ? [["Empresa", "Sector", "País", "Tipo de entidad", "Puntuación global", "Nivel"]]
        : [["Company", "Sector", "Country", "Entity type", "Global score", "Level"]];
      const standardName = standard?.fullName?.[lang] || standard?.shortName || "";
      const standardRow = lang === "es"
        ? ["Estándar evaluado", standardName, "", "", "", ""]
        : ["Standard assessed", standardName, "", "", "", ""];
      const summaryData = [[companyName, sector, country, entityType, `${globalScore}%`, globalLevel.label], standardRow];
      const domainHeader = lang === "es"
        ? [[], ["Dominio", "Artículo", "Puntuación", "Total controles", "Cumplen", "Brechas", "Críticas"]]
        : [[], ["Domain", "Article", "Score", "Total controls", "Compliant", "Gaps", "Critical gaps"]];
      const domainData = DOMAINS.map(d => {
        const score = calcDomainScore(d, answers, entityType);
        const dRows = allRows.filter(r => r.domainId === d.id);
        const compliant = dRows.filter(r => r.status === "ok").length;
        const gaps = dRows.filter(r => r.status === "no" || r.status === "partial").length;
        const crits = dRows.filter(r => r.critical && (r.status === "no" || r.status === "partial")).length;
        return [d.label[lang], typeof d.article === 'object' ? d.article[lang] : d.article, score !== null ? `${score}%` : "N/A", dRows.length, compliant, gaps, crits];
      });
      const summaryWs = XLSX.utils.aoa_to_sheet([...summaryHeader, ...summaryData, ...domainHeader, ...domainData]);
      summaryWs["!cols"] = [{ wch: 25 }, { wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 12 }, { wch: 15 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, summaryWs, lang === "es" ? "Resumen" : "Summary");

      const findingsHeader = lang === "es"
        ? [["Dominio", "Control", "Artículo", "Crítico", "Estado", "Recomendación"]]
        : [["Domain", "Control", "Article", "Critical", "Status", "Recommendation"]];
      const findingsData = allRows.map(r => {
        const st = statusStyle(r.status, r.critical);
        return [r.domainLabel, r.question, r.ref, r.critical ? (lang==="es"?"Sí":"Yes") : (lang==="es"?"No":"No"), st.label.replace("✓ ","").replace("⚠ ","").replace("✗ ","").replace("— ",""), (r.status === "no" || r.status === "partial") && r.fix ? r.fix : ""];
      });
      const findingsWs = XLSX.utils.aoa_to_sheet([...findingsHeader, ...findingsData]);
      findingsWs["!cols"] = [{ wch: 25 }, { wch: 55 }, { wch: 14 }, { wch: 8 }, { wch: 14 }, { wch: 60 }];
      XLSX.utils.book_append_sheet(wb, findingsWs, lang === "es" ? "Todos los controles" : "All controls");

      const gapsOnly = allRows.filter(r => r.status === "no" || r.status === "partial");
      const gapsHeader = lang === "es"
        ? [["Dominio", "Control", "Artículo", "Crítico", "Estado", "Recomendación"]]
        : [["Domain", "Control", "Article", "Critical", "Status", "Recommendation"]];
      const gapsData = gapsOnly.map(r => {
        const st = statusStyle(r.status, r.critical);
        return [r.domainLabel, r.question, r.ref, r.critical ? (lang==="es"?"Sí":"Yes") : (lang==="es"?"No":"No"), st.label.replace("⚠ ","").replace("✗ ",""), r.fix || ""];
      });
      const gapsWs = XLSX.utils.aoa_to_sheet([...gapsHeader, ...gapsData]);
      gapsWs["!cols"] = [{ wch: 25 }, { wch: 55 }, { wch: 14 }, { wch: 8 }, { wch: 14 }, { wch: 60 }];
      XLSX.utils.book_append_sheet(wb, gapsWs, lang === "es" ? "Brechas" : "Gaps");

      const date = new Date().toISOString().slice(0,10);
      XLSX.writeFile(wb, `${standard?.shortName || "Report"}_${companyName.replace(/\s+/g,"_")}_${date}.xlsx`);
    };
    document.head.appendChild(script);
  }

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800&family=DM+Mono:wght@400;500&display=swap');
        body{margin:0;background:${TH.bg}} *{box-sizing:border-box}
        @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        @media print{
          .no-print{display:none!important}
          .print-show{display:block!important}
          body{background:#fff!important}
          @page{margin:1.5cm}
        }`}
      </style>
      <Navbar lang={lang} onLangToggle={onLangToggle || (() => {})} onHome={onHome} showBreadcrumb={true} />
      <div style={{ minHeight:"100vh", background:TH.bg, padding:"2rem 1rem" }}>
        <div style={{ maxWidth:960, margin:"0 auto", animation:"fadeIn 0.3s ease" }}>

          {/* Toolbar */}
          <div className="no-print" style={{ display:"flex", alignItems:"center", gap:"0.75rem", marginBottom:"2rem", flexWrap:"wrap" }}>
            <button onClick={onBack}
              style={{ padding:"0.5rem 1rem", background:"transparent", border:`1px solid ${TH.border}`, borderRadius:8, color:TH.textMuted, fontFamily:"'DM Mono',monospace", fontSize:"0.72rem", cursor:"pointer" }}>
              ← {lang==="es" ? "Volver al informe" : "Back to report"}
            </button>
            <h2 style={{ margin:0, flex:1, fontFamily:"'Syne',sans-serif", fontSize:"1.2rem", fontWeight:800, color:TH.text }}>{t.detailTitle}</h2>
            <button onClick={() => window.print()}
              style={{ padding:"0.5rem 1rem", background:TH.surface, border:`1px solid ${TH.border}`, borderRadius:8, color:TH.text, fontFamily:"'Syne',sans-serif", fontSize:"0.75rem", cursor:"pointer" }}>
              ⎙ {lang==="es" ? "Imprimir" : "Print"}
            </button>
            <button onClick={exportExcel}
              style={{ padding:"0.5rem 1rem", background:TH.navy, border:"none", borderRadius:8, color:"#ffffff", fontFamily:"'Syne',sans-serif", fontWeight:600, fontSize:"0.75rem", cursor:"pointer" }}>
              ⬇ {lang==="es" ? "Exportar Excel" : "Export Excel"}
            </button>
          </div>

          {/* Report header */}
          <div style={{ marginBottom:"2.5rem", paddingBottom:"1.5rem", borderBottom:`1px solid ${TH.border}` }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.5rem", flexWrap:"wrap", gap:"1rem" }}>
              <div>
                <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:TH.textMuted, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:"0.3rem" }}>
                  {lang==="es" ? `Informe de Cumplimiento ${standard?.shortName || ""} · ${formatDate("es")}` : `${standard?.shortName || ""} Compliance Report · ${formatDate("en")}`}
                </div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontSize:"1.6rem", fontWeight:800, color:TH.text, lineHeight:1.1 }}>{companyName}</div>
                <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.7rem", color:TH.textMuted, marginTop:"0.3rem" }}>
                  {lang==="es"
                    ? `Sector: ${sector}${standard?.hasEntityType !== false && entityType ? ` · Tipo de entidad: ${entityType}` : ""} · País: ${country}`
                    : `Sector: ${sector}${standard?.hasEntityType !== false && entityType ? ` · Entity type: ${entityType}` : ""} · Country: ${country}`}
                </div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"2.5rem", fontWeight:500, color:globalLevel.color, lineHeight:1 }}>{globalScore}%</div>
                <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.7rem", color:globalLevel.color, textTransform:"uppercase", letterSpacing:"0.1em" }}>{globalLevel.label}</div>
                <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:TH.textMuted, marginTop:"0.15rem" }}>{globalLevel.desc}</div>
              </div>
            </div>

            {/* Domain cards grid */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:"0.8rem" }}>
              {DOMAINS.map(d => {
                const score = calcDomainScore(d, answers, entityType);
                const level = scoreToLevel(score, lang);
                const dGaps = allRows.filter(r => r.domainId === d.id && (r.status==="no"||r.status==="partial")).length;
                const dCrit = allRows.filter(r => r.domainId === d.id && r.critical && (r.status==="no"||r.status==="partial")).length;
                const dCompliant = allRows.filter(r => r.domainId === d.id && r.status === "ok").length;
                return (
                  <div key={d.id} style={{ background:TH.surfaceWhite, border:`1px solid ${TH.border}`, borderRadius:10, padding:"1rem" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"0.5rem" }}>
                      <span style={{ color:TH.textIcon, fontSize:"0.85rem" }}>{d.icon}</span>
                      <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.95rem", fontWeight:500, color:level.color }}>{score !== null ? `${score}%` : "—"}</span>
                    </div>
                    <div style={{ fontSize:"0.72rem", color:TH.textSub, fontWeight:600, marginBottom:"0.2rem", lineHeight:1.3 }}>{d.label[lang]}</div>
                    <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.57rem", color:TH.textIcon, marginBottom:"0.5rem" }}>{typeof d.article === 'object' ? d.article[lang] : d.article}</div>
                    {dCompliant > 0 && <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:"#1a5c3a", marginBottom:"0.15rem" }}>{lang==="es" ? `${dCompliant} cumple${dCompliant>1?"n":""}` : `${dCompliant} compliant`}</div>}
                    {dGaps > 0 && <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.65rem", color: dCrit > 0 ? "#b00020" : "#b07a00" }}>{lang==="es" ? `${dGaps} ${dGaps>1?"brechas":"brecha"}${dCrit>0?` · ${dCrit} crítica${dCrit>1?"s":""}`:""}`:`${dGaps} gap${dGaps>1?"s":""}${dCrit>0?` · ${dCrit} critical`:""}`}</div>}
                    <div style={{ marginTop:"0.6rem", height:3, background:TH.border, borderRadius:2 }}>
                      {score !== null && <div style={{ height:"100%", width:`${score}%`, background:level.color, borderRadius:2 }} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stats bar */}
          <div style={{ display:"flex", gap:"0.8rem", marginBottom:"1.5rem", flexWrap:"wrap" }}>
            {[
              { label: lang==="es" ? "Total controles" : "Total controls", val: totalRows, color: TH.accent },
              { label: lang==="es" ? "Cumplen" : "Compliant",              val: okRows,    color: "#1a5c3a" },
              { label: lang==="es" ? "Brechas" : "Gaps",                   val: gapRows,   color: "#b00020" },
            ].map(s => (
              <div key={s.label} style={{ flex:"1 1 100px", background:TH.surfaceWhite, border:`1px solid ${TH.border}`, borderRadius:8, padding:"0.75rem 1rem", textAlign:"center" }}>
                <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"1.4rem", fontWeight:500, color:s.color }}>{s.val}</div>
                <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:TH.textMuted, textTransform:"uppercase", letterSpacing:"0.1em" }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="no-print" style={{ display:"flex", gap:"0.5rem", marginBottom:"1.5rem", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ display:"flex", gap:"0.5rem", flexWrap:"wrap" }}>
              {[{key:"all",label:t.filterAll},{key:"gaps",label:t.filterGaps},{key:"ok",label:t.filterOk},{key:"critical",label:t.filterCritical}].map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)}
                  style={{ padding:"0.45rem 1rem", background: filter===f.key?"#1a5c3a18":"transparent", border:`1px solid ${filter===f.key?"#1a5c3a":TH.border}`, borderRadius:20, color: filter===f.key?"#1a5c3a":TH.textMuted, fontFamily:"'DM Mono',monospace", fontSize:"0.68rem", cursor:"pointer", transition:"all 0.15s" }}>
                  {f.label}
                </button>
              ))}
            </div>
            <div style={{ display:"flex", gap:"0.4rem" }}>
              <button onClick={() => setExpandedDomains(Object.fromEntries(DOMAINS.map(d => [d.id, true])))}
                style={{ padding:"0.45rem 0.9rem", background:"transparent", border:`1px solid ${TH.border}`, borderRadius:20, color:TH.textMuted, fontFamily:"'DM Mono',monospace", fontSize:"0.65rem", cursor:"pointer" }}>
                {lang==="es" ? "⊞ Expandir todo" : "⊞ Expand all"}
              </button>
              <button onClick={() => setExpandedDomains(Object.fromEntries(DOMAINS.map(d => [d.id, false])))}
                style={{ padding:"0.45rem 0.9rem", background:"transparent", border:`1px solid ${TH.border}`, borderRadius:20, color:TH.textMuted, fontFamily:"'DM Mono',monospace", fontSize:"0.65rem", cursor:"pointer" }}>
                {lang==="es" ? "⊟ Contraer todo" : "⊟ Collapse all"}
              </button>
            </div>
          </div>

          {/* Domain accordions */}
          {grouped.length === 0 ? (
            <div style={{ textAlign:"center", color:TH.textMuted, fontFamily:"'DM Mono',monospace", fontSize:"0.8rem", padding:"3rem" }}>
              {lang==="es" ? "No hay controles para mostrar con este filtro." : "No controls to show with this filter."}
            </div>
          ) : grouped.map(g => {
            const isOpen = expandedDomains[g.id] !== false;
            const gapCount = g.rows.filter(r => r.status==="no"||r.status==="partial").length;
            return (
              <div key={g.id} style={{ marginBottom:"0.75rem", background:TH.surfaceWhite, border:`1px solid ${TH.border}`, borderRadius:10, overflow:"hidden" }}>
                <button onClick={() => toggleDomain(g.id)} className="no-print"
                  style={{ width:"100%", display:"flex", alignItems:"center", gap:"0.75rem", padding:"0.9rem 1.1rem", background:"transparent", border:"none", cursor:"pointer", textAlign:"left" }}>
                  <span style={{ fontSize:"0.9rem" }}>{g.icon}</span>
                  <span style={{ flex:1, fontFamily:"'Syne',sans-serif", fontSize:"0.82rem", fontWeight:700, color:TH.text }}>{g.label}</span>
                  <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.62rem", color:TH.textMuted }}>{g.article}</span>
                  {gapCount > 0 && <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.62rem", color:"#b00020", marginLeft:"0.5rem" }}>{gapCount} {lang==="es"?(gapCount>1?"brechas":"brecha"):(gapCount>1?"gaps":"gap")}</span>}
                  <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.7rem", color:TH.textMuted, marginLeft:"0.5rem" }}>{isOpen?"▲":"▼"}</span>
                </button>
                <div className="print-show" style={{ display:"none", padding:"0.6rem 1.1rem", borderBottom:`1px solid ${TH.border}` }}>
                  <strong style={{ fontFamily:"'Syne',sans-serif", fontSize:"0.82rem", color:TH.text }}>{g.icon} {g.label}</strong>
                  <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.62rem", color:TH.textMuted, marginLeft:"0.75rem" }}>{g.article}</span>
                </div>
                {isOpen && (
                  <div style={{ borderTop:`1px solid ${TH.border}` }}>
                    {g.rows.map((r, i) => {
                      const st = statusStyle(r.status, r.critical);
                      const isGap = r.status === "no" || r.status === "partial";
                      return (
                        <div key={i} style={{ padding:"0.75rem 1.1rem", borderBottom: i<g.rows.length-1?`1px solid ${TH.border}40`:"none", background: r.critical&&isGap?"#fff0f020":"transparent", borderLeft: isGap ? "2px solid #d6d2ca" : "2px solid transparent", paddingLeft: isGap ? "1rem" : "1.1rem" }}>
                          {/* Top row: question (flex:1) + status (fixed width, right-aligned) */}
                          <div style={{ display:"flex", alignItems:"flex-start", gap:"1rem" }}>
                            <div style={{ flex:1, fontSize:"0.83rem", color:TH.textSub, lineHeight:1.6 }}>
                              {r.question}
                              {r.critical && isGap && <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:"#b00020", marginLeft:"0.35rem" }}>⚠</span>}
                              {!showRefPrefix && !showSoc2RefPrefix && (
                                <a href={artUrl(r.ref, lang)} target="_blank" rel="noopener noreferrer"
                                  style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.65rem", color:TH.accent, textDecoration:"none", borderBottom:`1px dotted ${TH.accent}`, whiteSpace:"nowrap", marginLeft:"0.5rem" }}>
                                  {r.ref} ↗
                                </a>
                              )}
                            </div>
                            <div style={{ width:120, flexShrink:0, textAlign:"right" }}>
                              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.68rem", color:st.color, whiteSpace:"nowrap", fontWeight:r.status==="no"?"600":"400" }}>
                                {st.label}
                              </span>
                            </div>
                          </div>
                          {/* Bottom row: recommendation — same two-column layout as top row */}
                          {isGap && r.fix && (
                            <div style={{ display:"flex", alignItems:"flex-start", gap:"1rem", marginTop:"0.4rem" }}>
                              <div style={{ flex:1, display:"flex", gap:"0.4rem", alignItems:"baseline" }}>
                                <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.62rem", color:TH.accent, flexShrink:0, fontWeight:500 }}>→ {lang==="es"?"Recomendación:":"Recommendation:"}</span>
                                <span style={{ fontSize:"0.78rem", color:TH.textMuted, lineHeight:1.5, fontStyle:"italic" }}>{r.fix}</span>
                              </div>
                              <div style={{ width:120, flexShrink:0 }} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <div className="no-print" style={{ marginTop:"2rem", textAlign:"center" }}>
            <button onClick={onBack}
              style={{ padding:"0.65rem 2rem", background:"transparent", border:`1px solid ${TH.border}`, borderRadius:8, color:TH.textMuted, fontFamily:"'Syne',sans-serif", fontSize:"0.85rem", cursor:"pointer" }}>
              ← {lang==="es" ? "Volver al informe" : "Back to report"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── ASSESSMENT ───────────────────────────────────────────────────────────────
function NIS2Assessment({ standard, onHome, lang, onLangToggle }) {
  const DOMAINS = standard.domains;

  // ── Synchronous initial session resolution (lazy initializer, runs once) ──
  const [_init] = useState(() => {
    let all = storage.list(COLLECTIONS.ASSESSMENTS)
      .filter(s => s.id.startsWith(standard.id + "__"));
    const sid = all.length === 0
      ? `${standard.id}__${Date.now().toString(36)}`
      : all.length === 1 ? all[0].id : null;
    if (all.length === 0 && sid) {
      const now = new Date().toISOString();
      storage.save(COLLECTIONS.ASSESSMENTS, sid, { lang, step: "intro", companyName: "", sector: "", entityType: "", country: "", activeDomain: 0, answers: {}, assessmentMode: null });
      all = [{ id: sid, meta: { createdAt: now, updatedAt: now } }];
    }
    const saved = sid ? (storage.load(COLLECTIONS.ASSESSMENTS, sid) || {}) : {};
    return { all, sid, saved };
  });

  // ── Session state ─────────────────────────────────────────────────────────
  const [sessionId, setSessionId]               = useState(_init.sid);
  const [sessions, setSessions]                 = useState(_init.all);

  const [assessmentMode, setAssessmentMode] = useState(_init.saved.assessmentMode || null);
  const { getQuestions, calcDomainScore, calcGlobalScore, getGaps, countAnswered } = (() => {
    const noET = standard.hasEntityType === false;
    const getQ = (d, et) => {
      let qs = noET ? d.questions : d.questions.filter(q => standard.scopeFilter ? standard.scopeFilter(q.scope, et, standard.modeSelector ? assessmentMode : undefined) : (q.scope === "all" || q.scope === et.toLowerCase() || et === "Esencial" || et === "Essential"));
      if (standard.modeSelector && assessmentMode === "lite") qs = qs.filter(q => q.lite);
      return qs;
    };
    const calcDS = (d, ans, et) => {
      const qs = getQ(d, et); let total = 0, earned = 0;
      for (const q of qs) { const a = ans[q.id]; if (!a || a === "na") continue; const w = q.critical ? 2 : 1; total += w; earned += (a==="yes"?1:a==="partial"?0.5:0)*w; }
      return total === 0 ? null : Math.round((earned/total)*100);
    };
    const calcGS = (ans, et) => { let total=0,earned=0; for(const d of DOMAINS){for(const q of getQ(d,et)){const a=ans[q.id];if(!a||a==="na")continue;const w=q.critical?2:1;total+=w;earned+=(a==="yes"?1:a==="partial"?0.5:0)*w;}} return total===0?0:Math.round((earned/total)*100); };
    const getG = (d, ans, et) => getQ(d,et).filter(q=>{const a=ans[q.id];return a==="no"||a==="partial";});
    const countA = (ans, et) => { let total=0,answered=0; for(const d of DOMAINS){const qs=getQ(d,et);total+=qs.length;answered+=qs.filter(q=>ans[q.id]).length;} return {total,answered}; };
    return { getQuestions:getQ, calcDomainScore:calcDS, calcGlobalScore:calcGS, getGaps:getG, countAnswered:countA };
  })();

  const [step, setStep]               = useState(_init.saved.step && _init.saved.step !== "results" ? _init.saved.step : "intro");
  const [companyName, setCompanyName] = useState(_init.saved.companyName || "");
  const [sector, setSector]           = useState(_init.saved.sector || "");
  const [entityType, setEntityType]   = useState(_init.saved.entityType || "");
  const [country, setCountry]         = useState(_init.saved.country || "");
  const [activeDomain, setActiveDomain] = useState(_init.saved.activeDomain || 0);
  const [answers, setAnswers]         = useState(_init.saved.answers || {});
  const [report, setReport]           = useState("");
  const [loading, setLoading]         = useState(false);
  const [, setHasSaved]               = useState(false);
  const [answersSnapshot, setAnswersSnapshot] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // true = skip first effect run (already loaded from _init.saved synchronously)
  const firstLoad        = useRef(_init.sid !== null);
  const isLoadingSession = useRef(false);
  const prevSessionId    = useRef(null); // saved when user opens picker mid-session (for cancel)

  // Load session data when sessionId changes (skip first run if already loaded)
  useEffect(() => {
    if (firstLoad.current) { firstLoad.current = false; return; }
    if (!sessionId) return;
    isLoadingSession.current = true;
    const sv = storage.load(COLLECTIONS.ASSESSMENTS, sessionId) || {};
    setStep(sv.step && sv.step !== "results" ? sv.step : "intro");
    setCompanyName(sv.companyName || "");
    setSector(sv.sector || "");
    setEntityType(sv.entityType || "");
    setCountry(sv.country || "");
    setActiveDomain(sv.activeDomain || 0);
    setAnswers(sv.answers || {});
    setAssessmentMode(sv.assessmentMode || null);
    setReport("");
    setAnswersSnapshot(null);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save session on every state change (skip during session switches)
  useEffect(() => {
    if (!sessionId) return;
    if (isLoadingSession.current) { isLoadingSession.current = false; return; }
    storage.save(COLLECTIONS.ASSESSMENTS, sessionId, { lang, step, companyName, sector, entityType, country, activeDomain, answers, assessmentMode });
  }, [sessionId, lang, step, companyName, sector, entityType, country, activeDomain, answers, assessmentMode]);

  const t = lang ? { ...T[lang], ...(standard.i18n?.[lang] || {}) } : { ...T.es, ...(standard.i18n?.es || {}) };
  const etArg = standard.hasEntityType === false ? "" : (entityType || "all");
  const { total, answered } = countAnswered(answers, etArg);
  const progress = total > 0 ? Math.round((answered / total) * 100) : 0;
  const globalScore = calcGlobalScore(answers, etArg);
  const globalLevel = scoreToLevel(globalScore, lang || "es");

  const SECTORS = standard.sectors || { es: [], en: [] };
  const COUNTRIES = standard.countries || { es: [], en: [] };

  async function handleGenerate() {
    const currentSnapshot = JSON.stringify(answers);
    if (report && answersSnapshot === currentSnapshot) { setStep("results"); window.scrollTo({ top:0, behavior:"smooth" }); return; }
    setLoading(true); setStep("results"); window.scrollTo({ top:0, behavior:"smooth" });
    const r = await standard.generateReport(companyName, sector, etArg, country, answers, lang);
    setReport(r); setAnswersSnapshot(currentSnapshot); setLoading(false);
  }

  function reset() {
    if (sessionId) storage.delete(COLLECTIONS.ASSESSMENTS, sessionId);
    const remaining = storage.list(COLLECTIONS.ASSESSMENTS).filter(s => s.id.startsWith(standard.id + "__"));
    const newId = `${standard.id}__${Date.now().toString(36)}`;
    storage.save(COLLECTIONS.ASSESSMENTS, newId, { lang, step: "intro", companyName: "", sector: "", entityType: "", country: "", activeDomain: 0, answers: {}, assessmentMode: null });
    setSessions([...remaining, { id: newId, meta: { createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() } }]);
    setSessionId(newId);
    setAnswers({}); setReport(""); setCompanyName(""); setSector(""); setEntityType(""); setCountry(""); setActiveDomain(0); setStep("intro"); setHasSaved(false); setAnswersSnapshot(null); setAssessmentMode(null);
  }

  // ── Session helpers ────────────────────────────────────────────────────────
  function openPicker() {
    const all = storage.list(COLLECTIONS.ASSESSMENTS).filter(s => s.id.startsWith(standard.id + "__"));
    setSessions(all);
    prevSessionId.current = sessionId;
    setSessionId(null);
  }

  function loadSession(sid) {
    prevSessionId.current = null;
    setSessionId(sid);
  }

  function deleteSession(sid) {
    if (!window.confirm(lang === "es" ? "¿Eliminar esta sesión? Esta acción no se puede deshacer." : "Delete this session? This action cannot be undone.")) return;
    storage.delete(COLLECTIONS.ASSESSMENTS, sid);
    const remaining = storage.list(COLLECTIONS.ASSESSMENTS).filter(s => s.id.startsWith(standard.id + "__"));
    if (sid === sessionId) {
      if (remaining.length > 0) {
        setSessions(remaining);
        loadSession(remaining[0].id);
      } else {
        const newId = `${standard.id}__${Date.now().toString(36)}`;
        storage.save(COLLECTIONS.ASSESSMENTS, newId, { lang, step: "intro", companyName: "", sector: "", entityType: "", country: "", activeDomain: 0, answers: {}, assessmentMode: null });
        setSessions([{ id: newId, meta: { createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() } }]);
        prevSessionId.current = null;
        setSessionId(newId);
        setStep("intro"); setCompanyName(""); setSector(""); setEntityType(""); setCountry("");
        setActiveDomain(0); setAnswers({}); setAssessmentMode(null); setReport(""); setHasSaved(false); setAnswersSnapshot(null);
      }
    } else {
      setSessions(remaining);
    }
  }

  function createNewSession() {
    const existing = storage.list(COLLECTIONS.ASSESSMENTS).filter(s => s.id.startsWith(standard.id + "__"));
    const newId = `${standard.id}__${Date.now().toString(36)}`;
    storage.save(COLLECTIONS.ASSESSMENTS, newId, { lang, step: "intro", companyName: "", sector: "", entityType: "", country: "", activeDomain: 0, answers: {}, assessmentMode: null });
    setSessions([...existing, { id: newId, meta: { createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() } }]);
    prevSessionId.current = null;
    setSessionId(newId);
    setStep("intro"); setCompanyName(""); setSector(""); setEntityType(""); setCountry("");
    setActiveDomain(0); setAnswers({}); setAssessmentMode(null); setReport(""); setHasSaved(false); setAnswersSnapshot(null);
  }

  // ── Export helpers ─────────────────────────────────────────────────────────
  function exportSession(sid) {
    const raw = storage.load(COLLECTIONS.ASSESSMENTS, sid);
    if (!raw) return;
    const { _meta, ...data } = raw;
    const payload = { collection: COLLECTIONS.ASSESSMENTS, id: sid, data, exportedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const slug = (data.companyName || "sin-nombre").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase();
    const date = new Date().toISOString().slice(0, 10);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug}-assessment-${standard.id}-${date}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportMultipleSessions(sids) {
    const items = sids.map(sid => {
      const raw = storage.load(COLLECTIONS.ASSESSMENTS, sid);
      if (!raw) return null;
      const { _meta, ...data } = raw;
      return { id: sid, data };
    }).filter(Boolean);
    if (items.length === 0) return;
    const payload = { exportType: "multiple", collection: COLLECTIONS.ASSESSMENTS, items, exportedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const date = new Date().toISOString().slice(0, 10);
    const a = document.createElement("a");
    a.href = url;
    a.download = `assessments-${standard.id}-multiple-${date}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── SESSION PICKER ────────────────────────────────────────────────────────
  if (sessions.length > 1 && !sessionId) {
    return (
      <>
        <style>{FONTS}</style>
        <div style={{ minHeight:"100vh", background:TH.bg }}>
          <Navbar lang={lang} onLangToggle={onLangToggle} onHome={onHome} showBreadcrumb />
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 2rem" }}>
            <div style={{ maxWidth:560, width:"100%", animation:"fadeIn 0.3s ease" }}>
              <Eyebrow text={t.badge} />
              <h1 style={{ fontFamily:"'Syne',sans-serif", fontSize:"1.4rem", fontWeight:700, color:TH.text, lineHeight:1.2, margin:"0 0 0.5rem" }}>
                {lang === "es" ? "Evaluaciones guardadas" : "Saved assessments"}
              </h1>
              <p style={{ fontFamily:"'Syne',sans-serif", color:TH.textMuted, fontSize:"0.82rem", marginBottom:"1.5rem", lineHeight:1.65 }}>
                {lang === "es" ? "Elige una evaluación existente o inicia una nueva." : "Choose an existing assessment or start a new one."}
              </p>
              {selectedIds.length > 0 && (
                <div style={{ display:"flex", alignItems:"center", gap:"0.75rem", padding:"0.6rem 1rem", background:"#f0fdf4", border:"1px solid #86efac", borderRadius:8, marginBottom:"0.75rem", flexWrap:"wrap" }}>
                  <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.65rem", color:TH.accent, fontWeight:500 }}>
                    {selectedIds.length} {lang === "es" ? "seleccionadas" : "selected"}
                  </span>
                  <button onClick={() => exportMultipleSessions(selectedIds)}
                    style={{ background:TH.accent, color:"#fff", border:"none", borderRadius:6, padding:"0.3rem 0.65rem", fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", cursor:"pointer" }}>
                    {lang === "es" ? "Exportar seleccionadas" : "Export selected"}
                  </button>
                  <button onClick={() => exportMultipleSessions(sessions.map(s => s.id))}
                    style={{ background:"transparent", border:`1px solid ${TH.border}`, borderRadius:6, padding:"0.3rem 0.65rem", fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:TH.textMuted, cursor:"pointer" }}>
                    {lang === "es" ? "Exportar todas" : "Export all"}
                  </button>
                  <button onClick={() => setSelectedIds([])}
                    style={{ background:"transparent", border:`1px solid ${TH.border}`, borderRadius:6, padding:"0.3rem 0.65rem", fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:TH.textMuted, cursor:"pointer", marginLeft:"auto" }}>
                    {lang === "es" ? "Deseleccionar" : "Clear"}
                  </button>
                </div>
              )}
              <div style={{ display:"flex", flexDirection:"column", gap:"0.6rem", marginBottom:"1.25rem" }}>
                {sessions.map(s => {
                  const sData = storage.load(COLLECTIONS.ASSESSMENTS, s.id) || {};
                  const isActive = s.id === sessionId;
                  const isSelected = selectedIds.includes(s.id);
                  const { total: sTotal, answered: sAnswered } = sData.companyName
                    ? countAnswered(sData.answers || {}, standard.hasEntityType === false ? "" : (sData.entityType || "all"))
                    : { total: 0, answered: 0 };
                  const pct = sTotal > 0 ? Math.round((sAnswered / sTotal) * 100) : 0;
                  const dateStr = s.meta?.updatedAt
                    ? new Date(s.meta.updatedAt).toLocaleDateString(lang === "es" ? "es-ES" : "en-GB")
                    : null;
                  return (
                    <div key={s.id} onClick={() => loadSession(s.id)}
                      style={{ display:"flex", alignItems:"center", background:TH.surfaceWhite, border:`1px solid ${isSelected ? TH.accent : isActive ? TH.accent : TH.border}`, borderRadius:10, padding:"1rem", gap:"0.75rem", cursor:"pointer", transition:"border-color 0.15s" }}>
                      <input type="checkbox" checked={isSelected} onClick={e => e.stopPropagation()}
                        onChange={e => setSelectedIds(prev => e.target.checked ? [...prev, s.id] : prev.filter(x => x !== s.id))}
                        style={{ accentColor:TH.accent, cursor:"pointer", flexShrink:0 }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:TH.text, fontSize:"0.9rem", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {sData.companyName || (lang === "es" ? "Sin empresa" : "No company")}
                        </div>
                        <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:TH.textMuted, marginTop:"0.2rem" }}>
                          {[sData.sector, sTotal > 0 ? `${pct}% (${sAnswered}/${sTotal})` : null, dateStr].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      {isActive && <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.58rem", color:TH.accent, letterSpacing:"0.06em", flexShrink:0 }}>{lang === "es" ? "ACTIVA" : "ACTIVE"}</span>}
                      <button onClick={e => { e.stopPropagation(); exportSession(s.id); }}
                        style={{ background:"none", border:"none", cursor:"pointer", color:TH.textMuted, fontSize:"0.75rem", padding:"0.2rem 0.4rem", borderRadius:4, flexShrink:0 }}
                        title={lang === "es" ? "Exportar" : "Export"}>↓</button>
                      <button onClick={e => { e.stopPropagation(); deleteSession(s.id); }}
                        style={{ background:"none", border:"none", cursor:"pointer", color:TH.textMuted, fontSize:"0.8rem", padding:"0.2rem 0.4rem", borderRadius:4, flexShrink:0 }}
                        title={lang === "es" ? "Eliminar" : "Delete"}>✕</button>
                    </div>
                  );
                })}
              </div>
              <button onClick={createNewSession}
                style={{ width:"100%", padding:"0.9rem", background:TH.navy, border:"none", borderRadius:8, color:"#ffffff", fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:"0.88rem", cursor:"pointer", letterSpacing:"0.03em" }}>
                {lang === "es" ? "Nueva evaluación +" : "New assessment +"}
              </button>
              <button onClick={() => document.getElementById("import-session-file").click()}
                style={{ width:"100%", marginTop:"0.5rem", padding:"0.7rem", background:"transparent", border:`1px solid ${TH.border}`, borderRadius:8, color:TH.textMuted, fontFamily:"'DM Mono',monospace", fontSize:"0.65rem", cursor:"pointer", letterSpacing:"0.03em" }}>
                {lang === "es" ? "Importar JSON" : "Import JSON"}
              </button>
              <input id="import-session-file" type="file" accept=".json" style={{ display:"none" }} onChange={async e => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                try {
                  const result = await storage.importJSON(file);
                  if (result.collection !== COLLECTIONS.ASSESSMENTS || typeof result.data !== "object") {
                    alert(lang === "es" ? "El archivo no contiene una sesión de evaluación válida." : "The file does not contain a valid assessment session.");
                    return;
                  }
                  const sid = result.id.startsWith(standard.id + "__") ? result.id : `${standard.id}__${Date.now().toString(36)}`;
                  storage.save(COLLECTIONS.ASSESSMENTS, sid, result.data);
                  const all = storage.list(COLLECTIONS.ASSESSMENTS).filter(s => s.id.startsWith(standard.id + "__"));
                  setSessions(all);
                  loadSession(sid);
                } catch (err) {
                  alert(lang === "es" ? `Error al importar: ${err.message}` : `Import error: ${err.message}`);
                }
              }} />
              {prevSessionId.current && (
                <button onClick={() => { const prev = prevSessionId.current; prevSessionId.current = null; setSessionId(prev); }}
                  style={{ width:"100%", marginTop:"0.75rem", padding:"0.6rem", background:"transparent", border:`1px solid ${TH.border}`, borderRadius:8, color:TH.textMuted, fontFamily:"'Syne',sans-serif", fontSize:"0.82rem", cursor:"pointer" }}>
                  {lang === "es" ? "Cancelar" : "Cancel"}
                </button>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── INTRO ──────────────────────────────────────────────────────────────────
  if (step === "intro") return (
    <>
      <style>{FONTS}</style>
      <div style={{ minHeight:"100vh", background:TH.bg }}>
        <Navbar lang={lang} onLangToggle={onLangToggle} onHome={onHome} showBreadcrumb />
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 2rem" }}>
          <div style={{ maxWidth:560, width:"100%", animation:"fadeIn 0.4s ease" }}>
            <Eyebrow text={t.badge} />
            <h1 style={{ fontFamily:"'Syne',sans-serif", fontSize:"1.4rem", fontWeight:700, color:TH.text, lineHeight:1.1, marginBottom:"1rem", marginTop:0 }}>
              {t.title1} {t.title2}
            </h1>
            <p style={{ fontFamily:"'Syne',sans-serif", color:TH.textMuted, fontSize:"0.88rem", lineHeight:1.7, marginBottom:"2.5rem" }}>
              {t.subtitle}
            </p>
            {sessions.length >= 1 && (
              <div style={{ marginBottom:"1.25rem", padding:"0.75rem 1rem", background:TH.surfaceWhite, border:`1px solid ${TH.border}`, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:"0.5rem" }}>
                <div>
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.55rem", color:TH.accent, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:"0.15rem" }}>
                    {lang === "es" ? "SESIÓN ACTIVA" : "ACTIVE SESSION"}
                  </div>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontSize:"0.82rem", color:TH.text, fontWeight:600 }}>
                    {companyName || (lang === "es" ? "Nueva evaluación" : "New assessment")}
                  </div>
                </div>
                <div style={{ display:"flex", gap:"0.5rem" }}>
                  {sessions.length > 1 && (
                    <button onClick={openPicker}
                      style={{ background:"transparent", border:`1px solid ${TH.border}`, borderRadius:6, padding:"0.3rem 0.65rem", fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:TH.textMuted, cursor:"pointer" }}>
                      {lang === "es" ? "Cambiar" : "Switch"}
                    </button>
                  )}
                  <button onClick={() => exportSession(sessionId)}
                    style={{ background:"transparent", border:`1px solid ${TH.border}`, borderRadius:6, padding:"0.3rem 0.65rem", fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:TH.textMuted, cursor:"pointer" }}>
                    {lang === "es" ? "Exportar" : "Export"}
                  </button>
                  <button onClick={createNewSession}
                    style={{ background:"transparent", border:`1px solid ${TH.border}`, borderRadius:6, padding:"0.3rem 0.65rem", fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:TH.textMuted, cursor:"pointer" }}>
                    {lang === "es" ? "Nueva +" : "New +"}
                  </button>
                </div>
              </div>
            )}
            <div style={{ display:"flex", flexDirection:"column", gap:"1rem", marginBottom:"1.5rem" }}>
              <Field label={t.fieldCompany} value={companyName} onChange={setCompanyName} placeholder={t.placeholderCo} />
              <div>
                <LabelEl>{t.fieldSector}</LabelEl>
                <select value={sector} onChange={e => setSector(e.target.value)}
                  style={{ width:"100%", background:"#ffffff", border:"1px solid #d6d2ca", borderRadius:6, padding:"0.7rem 1rem", color: sector ? TH.text : TH.textMuted, fontFamily:"'Syne',sans-serif", fontSize:"0.88rem", outline:"none", cursor:"pointer", transition:"border-color 0.2s" }}
                  onFocus={e => e.target.style.borderColor="#1a5c3a"}
                  onBlur={e => e.target.style.borderColor="#d6d2ca"}>
                  <option value="">{t.placeholderSec}</option>
                  {(SECTORS[lang] || []).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {standard.hasEntityType !== false && (
              <div>
                <LabelEl>{t.fieldEntity}</LabelEl>
                <div style={{ display:"flex", gap:"0.5rem" }}>
                  {[
                    { val: t.essential    || (lang==="es" ? "Esencial"       : "Essential"),      tip: t.tipEssential },
                    { val: t.important    || (lang==="es" ? "Importante"     : "Important"),      tip: t.tipImportant },
                    t.undetermined === null ? null : { val: t.undetermined || (lang==="es" ? "No determinado" : "Not determined"), tip: t.tipUndetermined },
                  ].filter(Boolean).map(({ val, tip }) => (
                    <button key={val} onClick={() => setEntityType(val)} title={tip}
                      style={{ flex:1, padding:"0.6rem 0.4rem", background: entityType===val ? "#1a5c3a12" : "#ffffff", border:`1px solid ${entityType===val ? "#1a5c3a" : "#d6d2ca"}`, borderLeft:`3px solid ${entityType===val ? "#1a5c3a" : "transparent"}`, borderRadius:6, color: entityType===val ? "#1a5c3a" : TH.textMuted, fontFamily:"'DM Mono',monospace", fontSize:"0.65rem", cursor:"pointer", transition:"all 0.18s" }}>
                      {val}
                    </button>
                  ))}
                </div>
                {entityType && (
                  <p style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.65rem", color:TH.textMuted, margin:"0.5rem 0 0", lineHeight:1.5 }}>
                    {[
                      { val: t.essential    || (lang==="es" ? "Esencial"       : "Essential"),      tip: t.tipEssential },
                      { val: t.important    || (lang==="es" ? "Importante"     : "Important"),      tip: t.tipImportant },
                      { val: t.undetermined || (lang==="es" ? "No determinado" : "Not determined"), tip: t.tipUndetermined },
                    ].find(o => o.val === entityType)?.tip}
                  </p>
                )}
              </div>
              )}
              {standard.modeSelector && (
              <div>
                <LabelEl>{t.modeSelector?.label || (lang === "es" ? "Modo de evaluaci\u00f3n" : "Assessment mode")}</LabelEl>
                <div style={{ display:"flex", gap:"0.5rem" }}>
                  {["lite", "full"].map(mode => {
                    const label = t.modeSelector?.[mode] || mode;
                    const tip = mode === "lite" ? t.modeSelector?.tipLite : t.modeSelector?.tipFull;
                    return (
                      <button key={mode} onClick={() => setAssessmentMode(mode)} title={tip}
                        style={{ flex:1, padding:"0.6rem 0.4rem", background: assessmentMode===mode ? "#1a5c3a12" : "#ffffff", border:`1px solid ${assessmentMode===mode ? "#1a5c3a" : "#d6d2ca"}`, borderLeft:`3px solid ${assessmentMode===mode ? "#1a5c3a" : "transparent"}`, borderRadius:6, color: assessmentMode===mode ? "#1a5c3a" : "#6b7280", fontFamily:"'DM Mono',monospace", fontSize:"0.65rem", cursor:"pointer", transition:"all 0.18s" }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                {assessmentMode && (
                  <p style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.65rem", color:"#6b7280", margin:"0.5rem 0 0", lineHeight:1.5 }}>
                    {assessmentMode === "lite" ? t.modeSelector?.tipLite : t.modeSelector?.tipFull}
                  </p>
                )}
              </div>
              )}
              <div>
                <LabelEl>{t.fieldCountry}</LabelEl>
                <select value={country} onChange={e => setCountry(e.target.value)}
                  style={{ width:"100%", background:"#ffffff", border:"1px solid #d6d2ca", borderRadius:6, padding:"0.7rem 1rem", color: country ? TH.text : TH.textMuted, fontFamily:"'Syne',sans-serif", fontSize:"0.88rem", outline:"none", cursor:"pointer", transition:"border-color 0.2s" }}
                  onFocus={e => e.target.style.borderColor="#1a5c3a"}
                  onBlur={e => e.target.style.borderColor="#d6d2ca"}>
                  <option value="">{lang === "es" ? "Selecciona un país…" : "Select a country…"}</option>
                  {(COUNTRIES[lang] || []).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display:"flex", gap:"0.8rem", marginBottom:"2rem" }}>
              {[
                { label: t.statDomains,   val: DOMAINS.length },
                { label: t.statQuestions, val: standard.hasEntityType === false ? (standard.meta?.questionCount || countAnswered(answers, "").total) : (entityType ? countAnswered(answers, entityType).total : `~${standard.meta?.questionCount || 80}`) },
                { label: t.statTime,      val: standard.meta?.timeEstimate?.[lang] || "15–20 min" },
              ].map(({ label, val }) => (
                <div key={label} style={{ flex:1, background:TH.surface, border:`1px solid ${TH.border}`, borderRadius:8, padding:"0.8rem", textAlign:"center" }}>
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"1rem", fontWeight:500, color:TH.accent }}>{val}</div>
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.58rem", color:TH.textMuted, textTransform:"uppercase", letterSpacing:"0.1em", marginTop:"0.2rem" }}>{label}</div>
                </div>
              ))}
            </div>
            {(() => {
              const canStart = (standard.hasEntityType === false
                ? (companyName && sector && country)
                : (companyName && sector && entityType && country))
                && (!standard.modeSelector || assessmentMode);
              return (
                <button onClick={() => canStart && setStep("assess")}
                  style={{ width:"100%", padding:"1rem", background: canStart ? TH.navy : TH.border, border:"none", borderRadius:8, color: canStart ? "#ffffff" : TH.textMuted, fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:"0.9rem", cursor: canStart ? "pointer":"default", letterSpacing:"0.05em", transition:"all 0.2s" }}
                  onMouseEnter={e => { if (canStart) e.currentTarget.style.background="#1a3a5c"; }}
                  onMouseLeave={e => { if (canStart) e.currentTarget.style.background=TH.navy; }}>
                  {t.startBtn}
                </button>
              );
            })()}
          </div>
        </div>
      </div>
    </>
  );

  // ── ASSESS ────────────────────────────────────────────────────────────────
  if (step === "assess") {
    const domain = DOMAINS[activeDomain];
    const qs = getQuestions(domain, etArg);
    const domainAnswered = qs.filter(q => answers[q.id]).length;
    const domainComplete = domainAnswered === qs.length;
    const ANS_COLORS = { yes:"#1a5c3a", partial:"#b07a00", no:"#b00020", na:"#6b7280" };
    return (
      <>
        <style>{FONTS}</style>
        <div style={{ minHeight:"100vh", background:TH.bg, display:"flex", flexDirection:"column", fontFamily:"'Syne',sans-serif" }}>
          <Navbar lang={lang} onLangToggle={onLangToggle} onHome={onHome} showBreadcrumb />
          <div style={{ display:"flex", flex:1 }}>
            {/* Backdrop (mobile only) */}
            {isMobile && sidebarOpen && (
              <div onClick={() => setSidebarOpen(false)} style={{ position:"fixed", inset:0, zIndex:199, background:"rgba(0,0,0,0.3)" }} />
            )}
            {/* Sidebar */}
            <div style={{ ...(isMobile ? { position:"fixed", top:0, left:0, height:"100%", zIndex:200, paddingTop:"52px", width:sidebarOpen ? 280 : 0 } : { width:sidebarOpen ? 270 : 0 }), overflow:"hidden", transition:"width 0.25s ease", background:TH.bg, borderRight:`1px solid ${TH.border}`, display:"flex", flexDirection:"column", gap:"0.2rem", flexShrink:0 }}>
              <div style={{ width:270, paddingBottom:"1.5rem", display:"flex", flexDirection:"column", gap:"0.2rem", height:"100%", overflowY:"auto", boxSizing:"border-box" }}>
              <div style={{ position:"sticky", top:0, background:TH.bg, zIndex:1, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"1.5rem 0.75rem 0.8rem 1.5rem" }}>
                <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.68rem", color:TH.textMuted, letterSpacing:"0.12em", textTransform:"uppercase" }}>{t.domainsLabel}</span>
                <button onClick={() => setSidebarOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", padding:"0.2rem", display:"flex", alignItems:"center", borderRadius:4, flexShrink:0 }}>
                  <svg width="20" height="14" viewBox="0 0 20 14" fill="none" stroke={TH.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="0.75" y="0.75" width="18.5" height="12.5" rx="2"/>
                    <line x1="6" y1="0.75" x2="6" y2="13.25"/>
                  </svg>
                </button>
              </div>
              {DOMAINS.map((d, i) => {
                const s = calcDomainScore(d, answers, etArg);
                const level = scoreToLevel(s, lang);
                const isActive = i === activeDomain;
                const domQs = getQuestions(d, etArg);
                const isComplete = domQs.length > 0 && domQs.every(q => answers[q.id]);
                const gaps = getGaps(d, answers, etArg).length;
                return (
                  <button key={d.id} onClick={() => setActiveDomain(i)}
                    style={{ display:"flex", alignItems:"center", gap:"0.55rem", padding:"0.5rem 0.6rem", background: isActive ? TH.surfaceWhite : "transparent", border:`1px solid ${isActive ? TH.border : "transparent"}`, borderLeft:`3px solid ${isActive ? "#1a5c3a" : "transparent"}`, borderRadius:6, cursor:"pointer", textAlign:"left", transition:"all 0.15s" }}>
                    <span style={{ fontSize:"0.75rem", color: isActive ? "#1a5c3a" : TH.textIcon, flexShrink:0 }}>{d.icon}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:"0.78rem", color: isActive ? TH.text : TH.textMuted, fontWeight: isActive ? 500 : 400, whiteSpace:"normal", lineHeight:1.35 }}>{d.label[lang]}</div>
                      {isComplete && s !== null && (
                        <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.68rem", color: level.color }}>{s}%{gaps > 0 ? ` · ${gaps} ${lang==="es"?(gaps>1?"brechas":"brecha"):(gaps>1?"gaps":"gap")}` : " · OK"}</div>
                      )}
                    </div>
                    {isComplete && <span style={{ color:"#1a5c3a", fontSize:"0.65rem", flexShrink:0 }}>✓</span>}
                  </button>
                );
              })}
              <div style={{ padding:"0.8rem", marginTop:"auto", background:TH.surfaceWhite, border:`1px solid ${TH.border}`, borderRadius:8 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"0.4rem" }}>
                  <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.58rem", color:TH.textMuted }}>{t.progress}</span>
                  <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.58rem", color:"#1a5c3a" }}>{answered}/{total}</span>
                </div>
                <div style={{ height:3, background:TH.border, borderRadius:2 }}>
                  <div style={{ height:"100%", width:`${progress}%`, background:"#1a5c3a", borderRadius:2, transition:"width 0.3s" }} />
                </div>
              </div>
              </div>
            </div>
            {/* Reopen strip — desktop only */}
            {!isMobile && !sidebarOpen && (
              <div onClick={() => setSidebarOpen(true)} style={{ width:40, background:TH.bg, borderRight:`1px solid ${TH.border}`, display:"flex", flexDirection:"column", alignItems:"center", paddingTop:"1rem", flexShrink:0, cursor:"pointer" }}>
                <svg width="20" height="14" viewBox="0 0 20 14" fill="none" stroke={TH.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="0.75" y="0.75" width="18.5" height="12.5" rx="2"/>
                  <line x1="6" y1="0.75" x2="6" y2="13.25"/>
                </svg>
              </div>
            )}
            {/* Floating reopen button — mobile only */}
            {isMobile && !sidebarOpen && step === "assess" && (
              <button onClick={() => setSidebarOpen(true)} style={{ position:"fixed", top:"64px", left:"0.75rem", zIndex:100, background:TH.surfaceWhite, border:`1px solid ${TH.border}`, borderRadius:8, padding:"0.5rem 0.6rem", display:"flex", alignItems:"center", gap:"0.4rem", cursor:"pointer" }}>
                <svg width="20" height="14" viewBox="0 0 20 14" fill="none" stroke={TH.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="0.75" y="0.75" width="18.5" height="12.5" rx="2"/>
                  <line x1="6" y1="0.75" x2="6" y2="13.25"/>
                </svg>
                <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:TH.textMuted }}>{t.domainsLabel}</span>
              </button>
            )}
            {/* Main */}
            <div style={{ flex:1, padding:"2.5rem", overflowY:"auto" }}>
              <div style={{ marginBottom:"2rem" }}>
                <div style={{ display:"flex", alignItems:"center", gap:"0.8rem", marginBottom:"0.4rem" }}>
                  <span style={{ fontSize:"1.3rem", color:TH.accent }}>{domain.icon}</span>
                  <div>
                    <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:TH.textMuted, letterSpacing:"0.1em" }}>{typeof domain.article === 'object' ? domain.article[lang] : domain.article}</div>
                    <h2 style={{ fontFamily:"'Syne',sans-serif", fontSize:"1.1rem", fontWeight:700, color:TH.text, margin:0 }}>{domain.label[lang]}</h2>
                  </div>
                </div>
                <p style={{ color:TH.textMuted, fontSize:"0.78rem", margin:"0 0 1rem" }}>{domain.description[lang]}</p>
                <div style={{ height:1, background:TH.border }} />
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:"1rem", maxWidth:700 }}>
                {qs.map(q => (
                  <div key={q.id} style={{ background:TH.surfaceWhite, border:`1px solid ${TH.border}`, borderRadius:10, padding:"1.1rem", animation:"fadeIn 0.25s ease" }}>
                    <div style={{ display:"flex", gap:"0.5rem", alignItems:"flex-start", marginBottom:"0.9rem" }}>
                      {q.critical && <span style={{ flexShrink:0, fontFamily:"'DM Mono',monospace", fontSize:"0.58rem", background:"#b0002018", color:"#b00020", border:"1px solid #b0002035", borderRadius:4, padding:"0.12rem 0.4rem", marginTop:2 }}>{t.critical}</span>}
                      <div style={{ flex:1 }}>
                        <p style={{ margin:0, color:TH.textSub, fontSize:"0.9rem", lineHeight:1.65 }}>{q[lang]}</p>
                        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.63rem", color:TH.textIcon }}>{q.ref_title ? q.ref_title[lang] : q.ref}</span>
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:"0.5rem", flexWrap:"wrap" }}>
                      {[
                        { value:"yes",     label:t.ansYes,     color:ANS_COLORS.yes },
                        { value:"partial", label:t.ansPartial, color:ANS_COLORS.partial },
                        { value:"no",      label:t.ansNo,      color:ANS_COLORS.no },
                        { value:"na",      label:t.ansNA,      color:ANS_COLORS.na },
                      ].map(opt => {
                        const sel = answers[q.id] === opt.value;
                        return (
                          <button key={opt.value} onClick={() => setAnswers(prev => ({ ...prev, [q.id]: opt.value }))}
                            style={{ padding:"0.6rem 1.3rem", background: sel ? `${opt.color}22` : "transparent", border:`${sel ? "2px" : "1px"} solid ${sel ? opt.color : TH.border}`, borderLeft: sel ? `3px solid ${opt.color}` : `1px solid ${TH.border}`, borderRadius:100, color: sel ? opt.color : TH.textMuted, fontFamily:"'DM Mono',monospace", fontSize:"0.78rem", cursor:"pointer", transition:"all 0.15s", fontWeight: sel ? 500:400 }}>
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {/* Nav */}
              <div style={{ display:"flex", gap:"0.8rem", marginTop:"2rem", position:"sticky", bottom:0, background:TH.bg, borderTop:`1px solid ${TH.border}`, padding:"0.75rem 0", paddingRight:"2.5rem", zIndex:5 }}>
                {activeDomain > 0 && (
                  <button onClick={() => setActiveDomain(activeDomain - 1)}
                    style={{ padding:"0.65rem 1.3rem", background:"transparent", border:`1px solid ${TH.border}`, borderRadius:8, color:TH.textMuted, fontFamily:"'Syne',sans-serif", fontSize:"0.8rem", cursor:"pointer" }}>
                    {t.prevBtn}
                  </button>
                )}
                {activeDomain < DOMAINS.length - 1 ? (
                  <button onClick={() => { setActiveDomain(activeDomain + 1); window.scrollTo({ top:0, behavior:"smooth" }); }}
                    style={{ padding:"0.65rem 1.3rem", background: domainComplete ? TH.navy : TH.border, border:"none", borderRadius:8, color: domainComplete ? "#ffffff" : TH.textMuted, fontFamily:"'Syne',sans-serif", fontWeight:600, fontSize:"0.8rem", cursor:"pointer", marginLeft:"auto", transition:"all 0.2s" }}>
                    {t.nextBtn}
                  </button>
                ) : (
                  <button onClick={handleGenerate}
                    style={{ padding:"0.65rem 1.5rem", background: answered > 0 ? TH.navy : TH.border, border:"none", borderRadius:8, color: answered > 0 ? "#ffffff" : TH.textMuted, fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:"0.82rem", cursor: answered > 0 ? "pointer":"default", marginLeft:"auto", transition:"all 0.2s" }}>
                    {t.generateBtn}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── DETAIL ────────────────────────────────────────────────────────────────
  if (step === "detail") return (
    <DetailView
      standard={standard}
      answers={answers} entityType={etArg} lang={lang} t={t}
      companyName={companyName} sector={sector} country={country}
      globalScore={globalScore} globalLevel={globalLevel}
      onBack={() => setStep("results")}
      onHome={onHome}
      onLangToggle={onLangToggle}
      assessmentMode={standard.modeSelector ? assessmentMode : undefined}
    />
  );

  // ── RESULTS ───────────────────────────────────────────────────────────────
  const domainScores = {};
  for (const d of DOMAINS) domainScores[d.id] = calcDomainScore(d, answers, etArg);
  return (
    <>
      <style>{FONTS}</style>
      <div style={{ minHeight:"100vh", background:TH.bg, fontFamily:"'Syne',sans-serif" }}>
        <Navbar lang={lang} onLangToggle={onLangToggle} onHome={onHome} showBreadcrumb />
        <div style={{ padding:"3rem 2rem" }}>
          <div style={{ maxWidth:960, margin:"0 auto", animation:"fadeIn 0.4s ease" }}>
            {/* Header */}
            <div style={{ paddingBottom:"1.5rem", borderBottom:`1px solid ${TH.border}`, marginBottom:"2rem" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:"1rem" }}>
                <div>
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.62rem", color:TH.textMuted, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:"0.3rem" }}>{t.reportHeader} · {formatDate(lang)}</div>
                  <h1 style={{ fontFamily:"'Syne',sans-serif", fontSize:"1.9rem", fontWeight:800, color:TH.text, margin:"0 0 0.3rem" }}>{companyName}</h1>
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.7rem", color:TH.textMuted }}>
                    {lang==="es"
                      ? `Sector: ${sector}${standard.hasEntityType !== false && entityType ? ` · Tipo de entidad: ${entityType}` : ""} · País: ${country}`
                      : `Sector: ${sector}${standard.hasEntityType !== false && entityType ? ` · Entity type: ${entityType}` : ""} · Country: ${country}`}
                  </div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"2.8rem", fontWeight:500, color:globalLevel.color, lineHeight:1 }}>{globalScore}%</div>
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.72rem", color:globalLevel.color, textTransform:"uppercase", letterSpacing:"0.1em" }}>{globalLevel.label}</div>
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:TH.textMuted, marginTop:"0.2rem" }}>{globalLevel.desc}</div>
                </div>
              </div>
            </div>
            {/* Actions */}
            <div className="no-print" style={{ display:"flex", justifyContent:"flex-end", gap:"0.6rem", marginBottom:"1.5rem" }}>
              <button onClick={() => window.print()}
                style={{ padding:"0.65rem 1.3rem", background:TH.surface, border:`1px solid ${TH.border}`, borderRadius:8, color:TH.text, fontFamily:"'Syne',sans-serif", fontSize:"0.85rem", cursor:"pointer" }}>
                {lang === "es" ? "⎙ Imprimir" : "⎙ Print"}
              </button>
              <button onClick={() => { setStep("detail"); window.scrollTo({ top:0, behavior:"smooth" }); }}
                style={{ padding:"0.65rem 1.3rem", background:"#1a5c3a12", border:"1px solid #1a5c3a", borderRadius:8, color:"#1a5c3a", fontFamily:"'Syne',sans-serif", fontSize:"0.85rem", cursor:"pointer" }}>
                {t.detailBtn}
              </button>
            </div>
            {/* Domain grid */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:"0.8rem", marginBottom:"2rem" }}>
              {DOMAINS.map(d => {
                const s = domainScores[d.id];
                const level = scoreToLevel(s, lang);
                const gaps = getGaps(d, answers, etArg);
                const critGaps = gaps.filter(g => g.critical).length;
                const dqs = getQuestions(d, etArg);
                const compliant = dqs.filter(q => answers[q.id] === "yes").length;
                return (
                  <div key={d.id} style={{ background:TH.surfaceWhite, border:`1px solid ${TH.border}`, borderRadius:10, padding:"1rem" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"0.5rem" }}>
                      <span style={{ color:TH.textIcon, fontSize:"0.85rem" }}>{d.icon}</span>
                      <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.95rem", fontWeight:500, color:level.color }}>{s !== null ? `${s}%` : "—"}</span>
                    </div>
                    <div style={{ fontSize:"0.72rem", color:TH.textSub, fontWeight:600, marginBottom:"0.2rem", lineHeight:1.3 }}>{d.label[lang]}</div>
                    <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.57rem", color:TH.textIcon, marginBottom:"0.5rem" }}>{typeof d.article === 'object' ? d.article[lang] : d.article}</div>
                    {compliant > 0 && <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:"#1a5c3a", marginBottom:"0.15rem" }}>{lang==="es" ? `${compliant} cumple${compliant>1?"n":""}` : `${compliant} compliant`}</div>}
                    {gaps.length > 0 && <div style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.65rem", color: critGaps > 0 ? "#b00020" : "#b07a00" }}>{lang === "es" ? `${gaps.length} ${gaps.length>1?"brechas":"brecha"}${critGaps>0?` · ${critGaps} crítica${critGaps>1?"s":""}` : ""}` : `${gaps.length} gap${gaps.length>1?"s":""}${critGaps>0?` · ${critGaps} critical`:""}`}</div>}
                    <div style={{ marginTop:"0.6rem", height:3, background:TH.border, borderRadius:2 }}>
                      {s !== null && <div style={{ height:"100%", width:`${s}%`, background:level.color, borderRadius:2 }} />}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Report */}
            <div style={{ background:TH.surfaceWhite, border:`1px solid ${TH.border}`, borderRadius:12, padding:"2rem", marginBottom:"1.5rem" }}>
              {loading ? (
                <div style={{ display:"flex", alignItems:"center", gap:"1rem", padding:"2rem 0" }}>
                  <div style={{ width:18, height:18, border:`2px solid ${TH.border}`, borderTop:"2px solid #1a5c3a", borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
                  <span style={{ fontFamily:"'DM Mono',monospace", fontSize:"0.82rem", color:TH.textMuted }}>{t.loadingMsg(Object.values(answers).filter(a=>a==="no"||a==="partial").length)}</span>
                </div>
              ) : (
                <div><MD text={report} lang={lang} artUrlFn={standard.artUrl} /></div>
              )}
            </div>
            {/* Bottom actions */}
            <div className="no-print" style={{ display:"flex", gap:"0.8rem", flexWrap:"wrap", alignItems:"center" }}>
              <button onClick={() => { setStep("assess"); setActiveDomain(0); }}
                style={{ padding:"0.65rem 1.3rem", background:"transparent", border:`1px solid ${TH.border}`, borderRadius:8, color:TH.textMuted, fontFamily:"'Syne',sans-serif", fontSize:"0.85rem", cursor:"pointer" }}>
                {t.editBtn}
              </button>
              <button onClick={reset}
                style={{ padding:"0.65rem 1.3rem", background:"transparent", border:`1px solid ${TH.border}`, borderRadius:8, color:TH.textMuted, fontFamily:"'Syne',sans-serif", fontSize:"0.85rem", cursor:"pointer" }}>
                {t.newBtn}
              </button>
              <button onClick={() => { setStep("detail"); window.scrollTo({ top:0, behavior:"smooth" }); }}
                style={{ padding:"0.65rem 1.3rem", background:"#1a5c3a12", border:"1px solid #1a5c3a", borderRadius:8, color:"#1a5c3a", fontFamily:"'Syne',sans-serif", fontSize:"0.85rem", cursor:"pointer" }}>
                {t.detailBtn}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem("at-tools:lang") || "es");

  useEffect(() => { localStorage.setItem("at-tools:lang", lang); }, [lang]);

  if (selectedStandard) {
    return (
      <NIS2Assessment
        standard={selectedStandard}
        onHome={() => setSelectedStandard(null)}
        lang={lang}
        onLangToggle={setLang}
      />
    );
  }

  if (selectedTool === "gdpr-breach-register") {
    return <BreachRegister onHome={() => setSelectedTool(null)} lang={lang} onLangToggle={setLang} />;
  }

  return (
    <>
      <style>{FONTS}</style>
      <HomeScreen
        standards={STANDARDS}
        onSelect={setSelectedStandard}
        onSelectTool={setSelectedTool}
        lang={lang}
        onLangToggle={setLang}
      />
    </>
  );
}
